/**
 * The event registry — the single place an onboarding event name may be coined.
 *
 * Names are lowercase `verb_noun` and **stable once shipped**: renaming one
 * silently breaks every saved insight and funnel built on it, with no error
 * anywhere. Add a name here first, then wire the call site.
 *
 * `AnalyticsEventMap` is what makes `track` type-safe — a property that is not
 * in the map for that event is a compile error rather than a dashboard column
 * that is quietly always empty.
 */

/**
 * The steps of the `core` journey, in the order a person meets them.
 *
 * `email` / `verify` / `account` are the auth screens; the rest are the
 * workspace wizard's own steps and deliberately share their keys with
 * `OnboardingDraft['step']`, minus its terminal `done`. The wizard narrows its
 * step to this type at the call site, so adding a wizard step without deciding
 * its analytics name fails to compile.
 *
 * One order for the whole journey, not one per screen: `step_index` is what
 * makes "how far did this person get" a single `max()` over a journey, and two
 * numbering schemes would silently disagree at the seam.
 *
 * `verify` currently has no call site. Signup verifies by clicking the emailed
 * link — arriving at `ConfirmRegister` with a valid token *is* the verification —
 * so there is no code-entry screen in this flow. The key is kept because it
 * holds the slot in the ordering, and a code-entry signup screen would take it.
 */
export const CoreStep = {
  EMAIL: 'email',
  VERIFY: 'verify',
  ACCOUNT: 'account',
  DETAILS: 'details',
  TEMPLATE: 'template',
  ATTRIBUTION: 'attribution',
  INVITE: 'invite'
} as const

export type CoreStepKey = (typeof CoreStep)[keyof typeof CoreStep]

/**
 * The steps in journey order. Derived from {@link CoreStep} rather than written
 * out again — two lists would drift, and the wire values must not depend on
 * which one a call site happened to read.
 */
export const CORE_STEP_ORDER: readonly CoreStepKey[] = Object.values(CoreStep)

/** Position of a step in the whole journey. */
export const coreStepIndex = (step: CoreStepKey): number =>
  CORE_STEP_ORDER.indexOf(step)

/**
 * Which journey the events belong to. `core` is the only one that exists today;
 * each app adds its own (`crm`, …) without touching this one.
 */
export const JourneyScope = { CORE: 'core' } as const

export type JourneyScope = (typeof JourneyScope)[keyof typeof JourneyScope]

/**
 * How the journey was entered. `SIGNUP` starts at `email` and is the first-ever
 * workspace; `IN_APP` starts at `details` — an existing account adding another
 * workspace, which has no signup steps to report.
 */
export const JourneyEntry = {
  SIGNUP: 'signup',
  IN_APP: 'in_app'
} as const

export type JourneyEntry = (typeof JourneyEntry)[keyof typeof JourneyEntry]

/**
 * Why the user stopped being on the step.
 *
 * `TAB_HIDDEN` is not abandonment — it also covers an ordinary tab switch, which
 * is why it carries its own event. Only `PAGE_HIDDEN` and `NAVIGATED_AWAY` end a
 * journey.
 */
export const ExitType = {
  TAB_HIDDEN: 'tab_hidden',
  PAGE_HIDDEN: 'page_hidden',
  NAVIGATED_AWAY: 'navigated_away'
} as const

export type ExitType = (typeof ExitType)[keyof typeof ExitType]

/**
 * Which sign-in route the user took.
 *
 * Separate from {@link SignupMethod} on purpose — signing in is not a step of any
 * onboarding journey, and the two must not share a vocabulary that invites them
 * being counted together. Only `EMAIL_OTP` has call sites today; the others are
 * declared so the password and social sign-in paths slot in without renaming
 * anything already recorded.
 */
export const LoginMethod = {
  PASSWORD: 'password',
  EMAIL_OTP: 'email_otp',
  GOOGLE: 'google',
  APPLE: 'apple'
} as const

export type LoginMethod = (typeof LoginMethod)[keyof typeof LoginMethod]

/**
 * Which track the owner starts with at the handoff fork. `OVERVIEW` is the third
 * answer — straight into the workspace, choosing neither card — and is kept
 * distinct so it cannot overstate either card's pull.
 */
export const HandoffPath = {
  CRM: 'crm',
  WORKSPACE: 'workspace',
  OVERVIEW: 'overview'
} as const

export type HandoffPath = (typeof HandoffPath)[keyof typeof HandoffPath]

/**
 * The setup tasks of the sidebar checklist.
 *
 * These are the platform's own journey step keys — the ones that come back in
 * `GET /onboarding/journeys/{scope}/` and go out again as `step_key` on a
 * `set_step` PATCH. They are therefore not ours to rename: the value has to
 * match the manifest, which is why `INVITE` reads `invite_users` and `SETTINGS`
 * reads `workspace_settings` rather than the shorter names the sidebar uses.
 *
 * Declared here anyway, alongside the other wire values, because the widget
 * narrows the backend's step keys to this type — a step the platform sends that
 * this app has no card for is then a value the compiler can point at.
 */
export const ChecklistItem = {
  /** Workspace setup — shown to admins and owners. */
  INVITE: 'invite_users',
  ROLES: 'roles',
  AUTHENTICATION: 'authentication',
  SETTINGS: 'workspace_settings',
  /** Personal setup — shown to everyone else. */
  PROFILE: 'profile',
  SECURITY: 'security',
  APPS: 'apps'
} as const

export type ChecklistItem = (typeof ChecklistItem)[keyof typeof ChecklistItem]

/**
 * Who the checklist was addressing.
 *
 * On every checklist event, because the two are different products for different
 * people: an admin is asked to set a workspace up, a member is asked to finish
 * their own account. Their completion rates have nothing to say about each other,
 * and a single funnel over both would be the exact blend BR-36 forbids.
 */
export const ChecklistAudience = {
  ADMIN: 'admin',
  MEMBER: 'member'
} as const

export type ChecklistAudience =
  (typeof ChecklistAudience)[keyof typeof ChecklistAudience]

/**
 * Why the checklist was hidden for good.
 *
 * A fixed list rather than a text field: the answer is a reason to stop asking,
 * and BR-44 allows pseudonymous properties only — free text could carry anything.
 */
export const ChecklistDismissReason = {
  ALREADY_SET_UP: 'already_set_up',
  NOT_RELEVANT: 'not_relevant',
  LATER: 'later',
  NOT_INTERESTED: 'not_interested'
} as const

export type ChecklistDismissReason =
  (typeof ChecklistDismissReason)[keyof typeof ChecklistDismissReason]

/**
 * Which link of a checklist card was taken — the in-app page or the public docs.
 * Separate values because they mean different things: one is an attempt at the
 * task, the other is someone who wanted to read first.
 */
export const ChecklistClickTarget = {
  PRIMARY: 'primary',
  DOCS: 'docs'
} as const

export type ChecklistClickTarget =
  (typeof ChecklistClickTarget)[keyof typeof ChecklistClickTarget]

/**
 * Which control the user was refused on.
 *
 * The first group mirrors the wizard's own `StepErrorField`, so the wizard can
 * pass its value straight through — and adding a field there without adding it
 * here is a compile error at the call site. The rest belong to the auth screens.
 */
export const OnboardingErrorField = {
  NAME: 'name',
  HANDLE: 'handle',
  COMPANY_SIZE: 'companySize',
  INDUSTRY: 'industry',
  PRIORITY: 'priority',
  INVITES: 'invites',
  EMAIL: 'email',
  PASSWORD: 'password',
  /** The signup link's token — missing, malformed or already spent. */
  TOKEN: 'token'
} as const

export type OnboardingErrorField =
  (typeof OnboardingErrorField)[keyof typeof OnboardingErrorField]

/**
 * Time on a step, reported by everything that ends one.
 *
 * Two numbers, not one: BR-40 defines time-on-step as focused time only, but
 * the gap between the two is itself the signal that a step is being handed to
 * someone else (a security review, an IT ticket) rather than being worked on —
 * which is what BR-41 needs to set per-step "stuck" thresholds.
 */
export type StepTiming = {
  /** Time the step was on screen *and* the tab had focus. */
  focused_ms: number
  /** Wall-clock time on the step, focus or not. */
  wall_ms: number
}

/**
 * What this client is still allowed to record.
 *
 * The platform now owns every event that describes a *fact about the account*:
 * `organization_created`, `onboarding_profile_saved`, `provisioning_started` /
 * `provisioning_completed`, `activation_reached`, `domain_match_checked`,
 * `invitations_sent`, `auth_method_enabled`, and the whole
 * `onboarding_step_completed` / `_skipped` / `_failed` family, which it emits
 * from `set_step` on `PATCH /onboarding/journeys/{scope}/`. Emitting any of
 * them here would double-count it, so none of them appear below — adding one
 * back is a compile error at the call site, which is the point.
 *
 * What stays is what only a browser can see: which screen was rendered, which
 * control refused the user, what they clicked, and how they left.
 */
export type AnalyticsEventMap = {
  onboarding_started: { entry: JourneyEntry }
  onboarding_step_viewed: { step: CoreStepKey; step_index: number }
  onboarding_step_error: {
    step: CoreStepKey
    field: OnboardingErrorField
    error_code: string
  }
  onboarding_step_exited: {
    step: CoreStepKey
    exit_type: ExitType
  } & StepTiming
  /**
   * Only the two server-settled verdicts. A too-short slug is a client-side
   * rule that fires on every keystroke below the minimum — it reaches analytics
   * as `onboarding_step_error` when the user actually tries to continue, which is
   * the moment it costs them something.
   */
  onboarding_handle_checked: { verdict: 'free' | 'taken' }
  onboarding_abandoned: {
    step: CoreStepKey
    exit_type: ExitType
  } & StepTiming
  onboarding_completed: {
    invites_sent: number
    /**
     * The preset codename the workspace was seeded from — `no_data` for the
     * clean-workspace tile, which is a preset like any other as far as the
     * platform is concerned. A codename rather than the preset's id, because
     * this is read in a chart legend; and rather than its `name`, which is
     * localised and would split one industry across three language buckets.
     */
    industry: string
    /**
     * Wall-clock milliseconds from the journey's first screen to this one.
     * Wall-clock, not focused: the per-step timer is per step, and a
     * journey-wide focused total would need a second timer measuring something
     * no insight asks for. Per-step focused time is on the step events.
     */
    duration_ms: number
  }
  onboarding_path_chosen: { path: HandoffPath }
  onboarding_invite_link_copied: Record<string, never>

  /**
   * Signing in — deliberately **outside** the `onboarding_` namespace.
   *
   * Passwordless sign-in shares its screens with nothing in the signup flow, and
   * the people using it are overwhelmingly returning users. Recording it as an
   * onboarding step would put them in the signup funnel, which is exactly the
   * blend BR-36 forbids. A separate name means no insight can mix them by
   * accident.
   *
   * These also give BR-35's "second session within seven days" somewhere to be
   * measured from, once the CRM member definition is built.
   */
  /**
   * The sidebar setup checklist.
   *
   * `total_count` travels with every expand because the number of tasks is not
   * fixed: an item the user's access role or a feature flag hides is neither
   * shown nor counted, so "2 of 4" and "2 of 3" are different populations and a
   * bare `completed_count` would blend them.
   */
  onboarding_checklist_expanded: {
    audience: ChecklistAudience
    completed_count: number
    total_count: number
  }
  onboarding_checklist_collapsed: { audience: ChecklistAudience }
  /** The card on the right changed — the user is reading, not clicking. */
  onboarding_checklist_switched: {
    audience: ChecklistAudience
    item: ChecklistItem
  }
  onboarding_checklist_item_clicked: {
    audience: ChecklistAudience
    item: ChecklistItem
    target: ChecklistClickTarget
  }
  /**
   * Snoozed, not gone: the checklist returns by itself. Kept apart from
   * `dismissed` so a "come back later" is never counted as a rejection.
   */
  onboarding_checklist_hidden: {
    audience: ChecklistAudience
    snooze_days: number
  }
  onboarding_checklist_dismissed: {
    audience: ChecklistAudience
    reason: ChecklistDismissReason
  }

  login_code_requested: { method: LoginMethod; resend: boolean }
  login_completed: { method: LoginMethod; attempts: number }
  login_failed: { method: LoginMethod; error_code: string; attempts: number }
}

export type AnalyticsEventName = keyof AnalyticsEventMap

/**
 * Names reserved by the upstream spec but **not emitted yet**. Listed here so
 * nobody coins a different name for them later; deliberately not part of
 * `AnalyticsEventMap`, because an event in the map with no call site reads as
 * instrumented when it is not.
 *
 * `onboarding_help_video_played` — the checklist card shows a still image, not a
 * video. The name is waiting for the video.
 */
